import React from "react";
import { Outlet, Link } from "react-router-dom";
import { Twitter, Linkedin, Instagram, Facebook, Youtube, Mail, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import TickerTape from "@/components/TickerTape";
import Navbar from "@/components/Navbar";
import MarketOverview from "@/components/MarketOverview";
import TopBanner from "@/components/TopBanner";
import AuthModal from "@/components/AuthModal";

const SOCIALS = [
  { icon: Twitter, label: "X (Twitter)", href: "https://twitter.com/stewartcapfinance" },
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/stewartcapfinance" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/stewartcapfinance" },
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/stewartcapfinance" },
  { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@stewartcapfinance" },
];

const SHARE_TEXT = "Stewart Cap Finance — live US & Canadian market intelligence, watchlists & AI-powered insights.";

export default function Layout() {
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://stewartcapfinance.com";
  const emailHref = `mailto:?subject=${encodeURIComponent("Stewart Cap Finance")}&body=${encodeURIComponent(`${SHARE_TEXT}\n\n${shareUrl}`)}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${shareUrl}`)}`;
  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pt-9">
      <TickerTape />
      <Navbar />
      <MarketOverview />
      <TopBanner />
      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Outlet />
      </main>
      <AuthModal />
      <footer className="border-t border-border mt-16">
        <div className="max-w-[1500px] mx-auto px-6 py-6 text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-num" data-testid="footer-copyright">© {new Date().getFullYear()} Stewart Capital Investments. All rights reserved.</span>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <div className="flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase().split(" ")[0]}`}
                  className="w-8 h-8 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <span className="opacity-30 hidden sm:inline">|</span>
            <div className="flex items-center gap-2" data-testid="footer-share">
              <span className="text-[10px] uppercase tracking-wider font-num text-muted-foreground/70 hidden sm:inline">Share</span>
              <a
                href={emailHref}
                aria-label="Share via Email"
                data-testid="share-email"
                className="w-8 h-8 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on WhatsApp"
                data-testid="share-whatsapp"
                className="w-8 h-8 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-[#10B981] hover:border-[#10B981]/50 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={copyLink}
                aria-label="Copy link"
                data-testid="share-copy-link"
                className="w-8 h-8 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
              >
                <Link2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="mailto:info@stewartcapfinance.com" data-testid="footer-contact-link" className="hover:text-foreground transition-colors">Contact us</a>
            <span className="opacity-40">·</span>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-foreground transition-colors">Terms</Link>
            <span className="opacity-40">·</span>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-foreground transition-colors">Privacy</Link>
            <span className="opacity-40 hidden sm:inline">·</span>
            <span className="hidden sm:inline">Not financial advice.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
